import { getAuthHeaders } from "@/lib/auth-fetch";
import { API_BASE } from "@/lib/utils";

const CHAT_ENDPOINT = `${API_BASE}/api/admin/ai/aivory-one/chat`;

export async function sendAivoryOneMessage({
  message,
  eventId,
  conversationId = null,
  workflowRunId = null,
  history = [],
  attachments = [],
  confirmedTools = null,
}) {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), 90000) : null;
  let res;
  try {
    res = await fetch(CHAT_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify({
        message: message || "",
        event_id: eventId || null,
        conversation_id: conversationId,
        workflow_run_id: workflowRunId,
        history: history.slice(-12),
        attachments: attachments.map((a) => ({
          url: a.url,
          name: a.name || "",
          kind: a.kind || a.mime || "",
          assetId: a.assetId || a.id || null,
        })),
        confirmed_tools: Array.isArray(confirmedTools) ? confirmedTools : [],
      }),
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("AivoryONE (Beta) timed out — try a shorter request, or open an event first for page design.");
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
  const body = await res.json().catch(() => ({}));
  if (res.status === 403) {
    const err = new Error(body?.detail || "Upgrade to Studio for AivoryONE (Beta)");
    err.code = "plan_gated";
    throw err;
  }
  if (!res.ok) {
    throw new Error(body?.detail || `Chat failed (${res.status})`);
  }
  return body;
}

export async function listConversations() {
  const res = await fetch(`${API_BASE}/api/admin/ai/conversations`, {
    headers: getAuthHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || "Failed to load conversations");
  return body.conversations || [];
}

export async function listSuggestions(eventId = null) {
  const q = eventId ? `?event_id=${encodeURIComponent(eventId)}` : "";
  const res = await fetch(`${API_BASE}/api/admin/ai/suggestions${q}`, {
    headers: getAuthHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return [];
  return body.suggestions || [];
}

export async function fetchAiCredits() {
  const res = await fetch(`${API_BASE}/api/admin/ai/credits`, {
    headers: getAuthHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return null;
  return body;
}

export async function loadConversationMessages(conversationId) {
  const res = await fetch(`${API_BASE}/api/admin/ai/conversations/${encodeURIComponent(conversationId)}/messages`, {
    headers: getAuthHeaders(),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || "Failed to load messages");
  return body.messages || [];
}

export async function rollbackAiMutation(snapshotId) {
  const res = await fetch(`${API_BASE}/api/admin/ai/mutations/rollback`, {
    method: "POST",
    headers: { "content-type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ snapshot_id: snapshotId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.detail || "Rollback failed");
  return body;
}

/** @deprecated use sendAivoryOneMessage */
export const sendOrchestratorMessage = sendAivoryOneMessage;
